// Shared settings tab components — slider row and toggle row.
import type { ReactNode } from 'react';

export function SliderRow({
  label,
  description,
  value,
  min,
  max,
  step,
  onChange,
  muted,
}: {
  label: string;
  description?: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  muted?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="w-24">
        <span className="text-xs text-muted-foreground">{label}</span>
        {description && <p className="text-[11px] text-muted-foreground mt-0.5">{description}</p>}
      </div>
      <input
        type="range"
        aria-label={label}
        min={min}
        max={max}
        step={step}
        value={muted ? 0 : value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="flex-1 h-1.5 rounded-full appearance-none bg-secondary accent-primary cursor-pointer"
      />
      <span className="w-10 text-right text-xs text-muted-foreground tabular-nums">
        {muted ? '--' : `${Math.round(value * 100)}%`}
      </span>
    </div>
  );
}

export function ToggleRow({
  label,
  description,
  value,
  onChange,
}: {
  label: string;
  description?: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div>
        <span className="text-xs text-foreground">{label}</span>
        {description && <p className="text-[11px] text-muted-foreground mt-0.5">{description}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        aria-label={label}
        onClick={() => onChange(!value)}
        className={`relative inline-flex h-5 w-9 cursor-pointer items-center rounded-full transition-colors ${
          value ? 'bg-primary' : 'bg-secondary'
        }`}
      >
        <span
          className={`inline-block size-3.5 rounded-full bg-white transition-transform ${
            value ? 'translate-x-[18px]' : 'translate-x-[3px]'
          }`}
        />
      </button>
    </div>
  );
}

export function SegmentedRow<T extends string>({
  label,
  description,
  value,
  options,
  onChange,
}: {
  label: string;
  description?: string;
  value: T;
  options: ReadonlyArray<{ readonly value: T; readonly label: string }>;
  onChange: (v: T) => void;
}) {
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between">
        <span className="text-xs text-foreground">{label}</span>
        <div
          role="radiogroup"
          className="flex overflow-hidden rounded-md border border-border/60 text-[11px]"
        >
          {options.map((opt) => {
            const selected = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                // biome-ignore lint/a11y/useSemanticElements: button-styled segmented control; <input type="radio"> would break the visual treatment
                role="radio"
                aria-checked={selected}
                onClick={() => onChange(opt.value)}
                className={`cursor-pointer px-2 py-1 transition-colors ${
                  selected
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background/40 text-muted-foreground hover:text-foreground'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
      {description ? (
        <p className="text-[11px] text-muted-foreground mt-0.5">{description}</p>
      ) : null}
    </div>
  );
}
